import { useEffect, useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DIRTY_DROPPED } from "@/pages/tina/festivals/lib/page-copy";
import { pagePath } from "@/pages/tina/festivals/lib/paths";
import type { FestivalPageRead } from "@/types/tina-festival-page";

interface SplitPagesDialogProps {
  page: FestivalPageRead;
  /** The page holds unsaved edits, which a split drops. */
  dirty: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Moves the pages out; answers the new festival's id, or null when nothing moved. */
  onSplit: (urls: string[]) => Promise<string | null>;
}

/** Move some of the pages Tina read into a new festival. At least one page stays here. */
export function SplitPagesDialog({
  page,
  dirty,
  open,
  onOpenChange,
  onSplit,
}: SplitPagesDialogProps) {
  const [ticked, setTicked] = useState<string[]>([]);
  const [created, setCreated] = useState<string | null>(null);
  useEffect(() => {
    if (!open) {
      setTicked([]);
      setCreated(null);
    }
  }, [open]);

  const all = ticked.length === page.pages.length;
  const none = ticked.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Split pages into a new festival</DialogTitle>
          <DialogDescription>
            Tick the pages that are about another festival. They move to a new
            festival waiting for approval.
          </DialogDescription>
        </DialogHeader>
        {created === null && dirty ? (
          <p className="text-sm text-muted-foreground">{DIRTY_DROPPED}</p>
        ) : null}
        {created !== null ? (
          <div className="flex flex-col gap-2 text-sm">
            <p>Split. The pages moved to a new festival.</p>
            <Link to={pagePath(created)} className="underline">
              Open the new festival
            </Link>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {page.pages.map((read) => (
              <li key={read.url} className="flex items-center gap-2 text-sm">
                <Checkbox
                  aria-label={`Move ${read.url}`}
                  checked={ticked.includes(read.url)}
                  onCheckedChange={(checked) =>
                    setTicked(
                      checked === true
                        ? [...ticked, read.url]
                        : ticked.filter((u) => u !== read.url),
                    )
                  }
                />
                {read.site} · {read.url.replace(/^[a-z]+:\/\//i, "")}
              </li>
            ))}
          </ul>
        )}
        {created === null && all && page.pages.length > 0 ? (
          <p className="text-sm text-amber-700">Keep at least one page here.</p>
        ) : null}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {created === null ? "Cancel" : "Close"}
          </Button>
          {created === null ? (
            <Button
              disabled={none || all}
              onClick={() => {
                void onSplit(ticked).then((id) => setCreated(id));
              }}
            >
              Split
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
